import { FC, JSX, useCallback, useContext, useEffect, useState } from "react";
import * as ReactMapGL from "react-map-gl/mapbox";
import { Layer, Source } from "react-map-gl/mapbox";
import { InteractionEvent } from "mapbox-gl";
import { MappingContext } from '@app/mapping/MappingContext';
import { MapboxSupportAlert } from '@app/shared/mapping/MapboxSupportAlert';
import { resizeObserverManager } from "@app/shared/qes-standards/utility/ResizeObserverManager";
import { get } from 'lodash';
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/BenchmarkingFiltersContext";
import { CompetitiveInsightsColorTheme } from "@app/overviews/network-benchmarking-dashboard/filters/CompetitiveInsightsColorThemeContext";
import { CompetitiveInsightsMapContext, CompetitiveInsightsMapContextProvider } from '@app/overviews/network-benchmarking-dashboard/filters/CompetitiveInsightsMapContext';
import CompetitiveInsightsMapOverlapLegend from '@app/overviews/network-benchmarking-dashboard/filters/CompetitiveInsightsMapOverlapLegend';
import CompetitiveInsightsPopupWrapper from "@app/overviews/network-benchmarking-dashboard/filters/CompetitiveInsightsPopupWrapper";
import NetworkFilterControl from '@app/overviews/network-benchmarking-dashboard/filters/NetworkFilterControl';
import QuestProprietaryDataIcon from "@app/overviews/network-benchmarking-dashboard/filters/QuestProprietaryDataIcon";
import { CompetitiveInsightsMapOverlapTypeEnum } from '@app/overviews/network-benchmarking-dashboard/filters/shared/CompetitiveInsightsMapOverlapTypeEnum';
import { getMapOverlapColor } from '@app/overviews/network-benchmarking-dashboard/filters/shared/utility';
import { BenchmarkingLineOfBusinessEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared/BenchmarkingLineOfBusinessEnum";
import { BenchmarkingNetworkType } from "@app/overviews/network-benchmarking-dashboard/filters/shared/benchmarkingNetworkType";
import { BenchmarkingParentOrganizationType } from '@app/overviews/network-benchmarking-dashboard/filters/shared/benchmarkingParentOrganizationType';
import { QHPNetwork } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/QHPNetwork";
import { MedicareNetwork } from "@app/overviews/network-benchmarking-dashboard/medicare/filters/shared/MedicareNetwork";
import { MedicaidNetwork } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/shared/MedicaidNetwork";

interface CompetitiveInsightsMapProps {
  networkFiltersLoading: boolean;
  parentOrganizations: BenchmarkingParentOrganizationType[];
  selectedNetworkCount: number;
  showNetworks: boolean;
}

const WrappedCompetitiveInsightsMap = (props: CompetitiveInsightsMapProps): JSX.Element => {
  const mappingContext = useContext(MappingContext);
  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);
  const competitiveInsightsMapContext = useContext(CompetitiveInsightsMapContext);

  const [mapHeight, setMapHeight] = useState<number>(0);
  const [tooltipContent, setTooltipContent] = useState<JSX.Element>(null);
  const [viewState, setViewState] = useState<ReactMapGL.ViewState>(mappingContext.defaultViewState);

  const { getColor } = CompetitiveInsightsColorTheme.useMapColorTheme();

  useEffect(() => {
    const resizeCallback = (event: ResizeObserverEntry): void => {
      setMapHeight((event.target as HTMLDivElement)?.offsetHeight);
    }

    const mapContainer = document.getElementById("competitive-insights-dashboard__map-tab--map-container");

    if (!!mapContainer) {
      resizeObserverManager.observe(mapContainer, resizeCallback);

      return () => {
        resizeObserverManager.unobserve(mapContainer);
      }
    }
  }, []);

  const getMapOverlapData = (overlapType: CompetitiveInsightsMapOverlapTypeEnum) => {
    return get(competitiveInsightsMapContext.overlapCounties, overlapType);
  }

  const onMapLoad = useCallback((): void => {
    const map = competitiveInsightsMapContext.mapRef.current?.getMap();

    if (!map) return;

    competitiveInsightsMapContext.onMapLoad();
  }, []);

  const onMapMove = useCallback((viewState: ReactMapGL.ViewState): void => {
    setViewState({
      ...viewState,
      // Disables the map rotation changes (keyboard, mouse, touch)
      bearing: mappingContext.defaultBearing,
      // Disables the map pitch changes (keyboard, mouse, touch)
      pitch: mappingContext.defaultPitch
    });
  }, []);

  const buildTooltipContent = (e: InteractionEvent, showTable?: boolean): void => {
    const label = e?.feature?.properties?.countyName;
    const networks: BenchmarkingNetworkType[] = []

    if (showTable) {
      for (const network of benchmarkingFiltersContext.selectedNetworks) {
        if (network.networkCounties.some(nc => nc.countyId === e?.feature?.properties?.countyId)) {
          networks.push(network);
        }
      }
    }

    const getMedicareBody = (n: MedicareNetwork): JSX.Element => {
      const countyMembership = n.networkCounties.find(nc => nc.countyId === e?.feature?.properties?.countyId)?.membership;
      return (
        <tr key={n.contractId}>
          <td>
            <span className="competitive-insights__map--tooltip-network-name">
              {n.parentOrganization}
            </span>
          </td>
          <td>
            <div className="d-flex align-items-center">
              <span className="competitive-insights__map--tooltip-network-name d-inline-block">
                {n.contractId}
              </span>
              {n.isQesNetwork && (
                <span className="mt-1 d-inline">
                  <QuestProprietaryDataIcon />
                </span>
              )}
              {n.isSnpPlan && (
                <img className="d-block ms-1" src={`${window.customUIBaseUrl}/images/snp-icon.svg`} alt="" />
              )}
            </div>
          </td>
          <td>
            {countyMembership ? Number(countyMembership).toLocaleString() : "---"}
          </td>
        </tr>
      );
    }

    const getQHPBody = (n: QHPNetwork): JSX.Element => {
      return (
        <tr key={n.contractId}>
          <td>
            <span className="competitive-insights__map--tooltip-network-name">
              {n.parentOrganization}
            </span>
          </td>
          <td>
            <div className="d-flex align-items-center">
              <span className="competitive-insights__map--tooltip-network-name d-inline-block">
                {n.networkName}
              </span>
              {n.isQesNetwork && (
                <span className="mt-1 d-inline">
                  <QuestProprietaryDataIcon />
                </span>
              )}
              {n.dentalOnly && (
                <img className="d-block" src={`${window.customUIBaseUrl}/images/purple-tooth.svg`} alt="" />
              )}
            </div>
          </td>
        </tr>
      );
    }

    const getMedicaidBody = (n: MedicaidNetwork): JSX.Element => {
      return (
        <tr key={n.contractId}>
          <td>
            <span className="competitive-insights__map--tooltip-network-name">
              {n.parentOrganization}
            </span>
          </td>
          <td>
            <div className="d-flex align-items-center">
              <span className="competitive-insights__map--tooltip-network-name d-inline-block">
                {n.program}
              </span>
              {!n.disabled && (
                <span className="mt-1 d-inline">
                  <QuestProprietaryDataIcon />
                </span>
              )}
            </div>
          </td>
        </tr>
      );
    }

    switch (benchmarkingFiltersContext.selectedLineOfBusiness) {
      case BenchmarkingLineOfBusinessEnum.Marketplace:
        const qhpTable: JSX.Element = (
          <div>
            <strong>{label}</strong> <br />
            {showTable && (
              <table className="competitive-insights__map--tooltip-table">
                <thead>
                  <tr>
                    <th>Payer</th>
                    <th>Network Name</th>
                  </tr>
                </thead>
                <tbody>
                  {networks.map((n) => (
                    getQHPBody(n as QHPNetwork)
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );

        setTooltipContent(qhpTable);
        break;
      case BenchmarkingLineOfBusinessEnum.Medicare:
        const medicareTable: JSX.Element = (
          <div>
            <strong>{label}</strong> <br />
            {showTable && (
              <table className="competitive-insights__map--tooltip-table">
                <thead>
                  <tr>
                    <th>Payer</th>
                    <th>Contract</th>
                    <th>Membership</th>
                  </tr>
                </thead>
                <tbody>
                  {networks.map((n) => (
                    getMedicareBody(n as MedicareNetwork)
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );

        setTooltipContent(medicareTable);
        break;
      case BenchmarkingLineOfBusinessEnum.Medicaid:
        const medicaidTable: JSX.Element = (
          <div>
            <strong>{label}</strong> <br />
            {showTable && (
              <table className="competitive-insights__map--tooltip-table">
                <thead>
                  <tr>
                    <th>Payer</th>
                    <th>Program</th>
                  </tr>
                </thead>
                <tbody>
                  {networks.map((n) => (
                    getMedicaidBody(n as MedicaidNetwork)
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );

        setTooltipContent(medicaidTable);
        break;
      default:
        break;
    }
  }

  return (
    <MapboxSupportAlert>
      <div id="competitive-insights-dashboard__map-tab--map-container">
        <ReactMapGL.Map
          {...viewState}
          {...mappingContext.defaultMapProps}
          onLoad={onMapLoad}
          onMove={(e) => onMapMove(e.viewState)}
          ref={competitiveInsightsMapContext.mapRef}
        >
          {/* unselected (grey) */}
          <Source id="ci-map__unselected" type="geojson" data={competitiveInsightsMapContext.unselectedCounties}>
            {/* County fill layer. */}
            <Layer
              id="ci-map__unselected--fill"
              type="fill"
              paint={{
                "fill-color": getColor("unselectedCountyFillColor"),
                "fill-opacity": 1
              }}
            />
            {/* County outline layer. */}
            <Layer
              id="ci-map__unselected--outline"
              type="line"
              paint={{
                "line-color": "rgba(172, 172, 197, 1)",
                "line-offset": 0.5,
                "line-width": 1,
              }}
            />
            <CompetitiveInsightsPopupWrapper
              buildTooltipContent={buildTooltipContent}
              layerId="ci-map__unselected--fill"
              selectedNetworks={benchmarkingFiltersContext.selectedNetworks}
            >
              <div>
                {tooltipContent}
              </div>
            </CompetitiveInsightsPopupWrapper>
          </Source>

          {/* selected counties (blue) */}
          <Source id="ci-map__selected" type="geojson" data={competitiveInsightsMapContext.selectedCounties}>
            {/* County fill layer. */}
            <Layer
              id="ci-map__selected--fill"
              type="fill"
              paint={{
                "fill-color": getColor("selectedCountyFillColor"),
                "fill-opacity": 1
              }}
            />
            {/* County outline layer. */}
            <Layer
              id="ci-map__selected--outline"
              type="line"
              paint={{
                "line-color": "rgba(5, 110, 223, 1)",
                "line-offset": 0.5,
                "line-width": 1,
              }}
            />
            <CompetitiveInsightsPopupWrapper
              buildTooltipContent={buildTooltipContent}
              layerId="ci-map__selected--fill"
              selectedNetworks={benchmarkingFiltersContext.selectedNetworks}
            >
              <div>
                {tooltipContent}
              </div>
            </CompetitiveInsightsPopupWrapper>
          </Source>

          {/* hovered counties (yellow) */}
          <Source id="ci-map__hover" type="geojson" data={competitiveInsightsMapContext.hoveredCounties}>
            {/* County fill layer. */}
            <Layer
              id="ci-map__hover--fill"
              type="fill"
              paint={{
                "fill-color": "rgba(255, 247, 221, 1)",
                "fill-opacity": 1
              }}
            />
            {/* County outline layer. */}
            <Layer
              id="ci-map__hover--outline"
              type="line"
              paint={{
                "line-color": "rgba(255, 214, 84, 1)",
                "line-offset": 0.5,
                "line-width": 1,
              }}
            />
          </Source>

          {/* overlap */}
          {Object.values(CompetitiveInsightsMapOverlapTypeEnum).map((mapOverlapType, index) => (
            <Source key={index} id={`ci-map__overlap-${index}`} type="geojson" data={getMapOverlapData(mapOverlapType)}>
              {/* County fill layer. */}
              <Layer
                id={`ci-map__overlap--fill-${index}`}
                type="fill"
                paint={{
                  "fill-color": getColor(getMapOverlapColor(mapOverlapType)),
                  "fill-opacity": 1
                }}
              />
              {/* County outline layer. */}
              <Layer
                id={`ci-map__hover--outline-${index}`}
                type="line"
                paint={{
                  "line-color": "rgba(5, 110, 223, 1)",
                  "line-offset": 0.5,
                  "line-width": 1,
                }}
              />
              <CompetitiveInsightsPopupWrapper
                buildTooltipContent={buildTooltipContent}
                layerId={`ci-map__overlap--fill-${index}`}
                selectedNetworks={benchmarkingFiltersContext.selectedNetworks}
                showTable={true}
              >
                <div>
                  {tooltipContent}
                </div>
              </CompetitiveInsightsPopupWrapper>
            </Source>
          ))}

          {props.showNetworks && (
            <NetworkFilterControl
              mapHeight={mapHeight}
              networkFiltersLoading={props.networkFiltersLoading}
              parentOrganizations={props.parentOrganizations}
            />
          )}

          <CompetitiveInsightsMapOverlapLegend />
        </ReactMapGL.Map>
      </div>
    </MapboxSupportAlert>
  );
}

export const CompetitiveInsightsMap: FC<CompetitiveInsightsMapProps> = (props: CompetitiveInsightsMapProps): JSX.Element => {
  return (
    <CompetitiveInsightsColorTheme.Provider>
      <CompetitiveInsightsMapContextProvider>
        <WrappedCompetitiveInsightsMap {...props} />
      </CompetitiveInsightsMapContextProvider>
    </CompetitiveInsightsColorTheme.Provider>
  );
};